import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Users, ChatCircle, Heart, Flag, ShieldCheck, Prohibit, Trash, MagnifyingGlass, Crown, ChartBar, Scales, Tag, ChartLineUp, Clock, UsersThree, CurrencyEur, Repeat, FileText } from '@phosphor-icons/react';
import { LineChart } from '../components/LineChart';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { getPhotoSrc } from '../lib/avatar';
import api from '../lib/api';

function StatsCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center`} style={{ background: `${color}15` }}>
        <Icon size={24} weight="light" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white font-['Outfit']">{value}</p>
        <p className="text-xs text-[#9EABCE]">{label}</p>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [legalPages, setLegalPages] = useState({});
  const [editingLegal, setEditingLegal] = useState(null);
  const [legalTitle, setLegalTitle] = useState('');
  const [legalContent, setLegalContent] = useState('');
  const [promos, setPromos] = useState([]);
  const [savingLegal, setSavingLegal] = useState(false);
  const [orphanData, setOrphanData] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState(7);
  const [growthRange, setGrowthRange] = useState(30);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPages, setUsersPages] = useState(1);
  const [usersSort, setUsersSort] = useState('newest');
  const USERS_PER_PAGE = 25;

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/stats');
      setStats(data);
    } catch {}
  }, []);

  const fetchAudit = useCallback(async (action = '') => {
    setAuditLoading(true);
    try {
      const params = { limit: 100 };
      if (action) params.action = action;
      const { data } = await api.get('/admin/audit-log', { params });
      setAuditRows(data.rows || []);
    } catch {
      setAuditRows([]);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      const params = { page, limit: USERS_PER_PAGE, sort: usersSort };
      if (search) params.search = search;
      if (userStatusFilter && userStatusFilter !== 'all') params.status = userStatusFilter;
      const { data } = await api.get('/admin/users', { params });
      setUsers(data.users || []);
      setUsersPage(data.page || page);
      setUsersTotal(data.total || 0);
      setUsersPages(data.pages || 1);
    } catch {}
  }, [search, userStatusFilter, usersSort]);

  const fetchReports = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/reports');
      setReports(data.reports || []);
    } catch {}
  }, []);

  const fetchLegal = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/legal');
      const map = {};
      (data.pages || []).forEach(p => { map[p.type] = p; });
      setLegalPages(map);
    } catch {}
  }, []);

  const fetchPromos = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/promos');
      setPromos(data.promos || []);
    } catch {}
  }, []);

  const fetchAnalytics = useCallback(async (days = 7) => {
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get('/admin/events/summary', { params: { days } });
      setAnalytics(data);
    } catch {
      setAnalytics(null);
    }
    setAnalyticsLoading(false);
  }, []);

  const fetchGrowth = useCallback(async (days = 30) => {
    setGrowthLoading(true);
    try {
      const { data } = await api.get('/admin/analytics/growth', { params: { days } });
      setGrowth(data);
    } catch {
      setGrowth(null);
    }
    setGrowthLoading(false);
  }, []);

  useEffect(() => {
    if (!user?.is_admin) { navigate('/browse'); return; }
    setLoading(true);
    Promise.all([fetchStats(), fetchUsers(), fetchReports(), fetchLegal(), fetchPromos()]).finally(() => setLoading(false));
  }, [user, navigate, fetchStats, fetchUsers, fetchReports, fetchLegal, fetchPromos]);

  // Re-fetch users when status filter changes while on Users tab
  useEffect(() => {
    if (tab === 'users') fetchUsers();
  }, [userStatusFilter, usersSort, tab, fetchUsers]);

  const handleBan = async (userId) => {
    await api.put(`/admin/users/${userId}/ban`);
    fetchUsers();
  };

  const handleUnban = async (userId) => {
    await api.put(`/admin/users/${userId}/unban`);
    fetchUsers();
  };

  const handleVerify = async (userId) => {
    await api.put(`/admin/users/${userId}/verify`);
    fetchUsers();
  };

  const handleUnverify = async (userId) => {
    await api.put(`/admin/users/${userId}/unverify`);
    fetchUsers();
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Permanently delete this user?')) return;
    await api.delete(`/admin/users/${userId}`);
    fetchUsers();
    fetchStats();
  };

  const handleSaveLegal = async () => {
    if (!editingLegal) return;
    setSavingLegal(true);
    try {
      await api.put(`/admin/legal/${editingLegal}`, { title: legalTitle, content: legalContent });
      fetchLegal();
      setEditingLegal(null);
    } catch {}
    setSavingLegal(false);
  };

  const handleCreatePromo = async () => {
    try {
      await api.post('/admin/promo/create');
      fetchPromos();
    } catch {}
  };

  const handleTogglePromo = async (code) => {
    try {
      await api.put(`/admin/promo/${code}/toggle`);
      fetchPromos();
    } catch {}
  };

  const fetchOrphans = useCallback(async () => {
    setOrphanLoading(true);
    try {
      const { data } = await api.get('/admin/orphan-chats');
      setOrphanData(data);
    } catch (err) {
      console.error('[admin.orphan-chats] failed', err);
    }
    setOrphanLoading(false);
  }, []);

  const handlePurgeOrphans = async () => {
    if (!orphanData?.orphan_chat_count) return;
    if (!window.confirm(
      `Permanently delete ${orphanData.orphan_chat_count} orphan chats and ${orphanData.orphan_message_count} messages?\n\nThese belong to users who no longer exist in the database. This cannot be undone.`
    )) return;
    setPurging(true);
    try {
      const { data } = await api.post('/admin/orphan-chats/purge');
      alert(`Purged ${data.purged_chat_count} chats and ${data.purged_message_count} messages.`);
      await fetchOrphans();
      fetchStats();
    } catch (err) {
      alert('Purge failed: ' + (err.response?.data?.detail || err.message));
    }
    setPurging(false);
  };

  // Payment Rescue — manually grant Premium or sync from a Stripe session
  const [grantEmail, setGrantEmail] = useState('');
  const [syncSessionId, setSyncSessionId] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantResult, setGrantResult] = useState(null);

  const handleGrantPremium = async () => {
    if (!grantEmail.trim()) return;
    setGrantBusy(true); setGrantResult(null);
    try {
      const { data } = await api.post('/admin/grant-premium', { email: grantEmail.trim().toLowerCase() });
      setGrantResult({ ok: true, msg: `${data.email} is now Premium (matched=${data.matched})` });
      fetchStats(); fetchUsers();
    } catch (err) {
      setGrantResult({ ok: false, msg: err.response?.data?.detail || err.message });
    }
    setGrantBusy(false);
  };

  const handleSyncFromSession = async () => {
    if (!syncSessionId.trim()) return;
    setGrantBusy(true); setGrantResult(null);
    try {
      const { data } = await api.post('/admin/sync-premium-from-session', { session_id: syncSessionId.trim() });
      setGrantResult({ ok: true, msg: `Upgraded ${data.upgraded_user.email} from session ${data.session_id.slice(0, 20)}…` });
      fetchStats(); fetchUsers();
    } catch (err) {
      setGrantResult({ ok: false, msg: err.response?.data?.detail || err.message });
    }
    setGrantBusy(false);
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/browse')} className="flex items-center gap-2 text-[#9EABCE] hover:text-white mb-6 transition-colors">
          <ArrowLeft size={20} /> Back to Browse
        </button>

        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck size={32} weight="fill" className="text-[#D4AF37]" />
          <h1 className="text-2xl md:text-3xl font-medium tracking-tight text-white font-['Outfit']">Admin Dashboard</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: ChartBar },
            { id: 'analytics', label: 'Analytics', icon: ChartLineUp },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'reports', label: 'Reports', icon: Flag },
            { id: 'legal', label: 'Legal CMS', icon: Scales },
            { id: 'promos', label: 'Promos', icon: Tag },
            { id: 'audit', label: 'Audit Log', icon: FileText },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); if (t.id === 'users') fetchUsers(); if (t.id === 'reports') fetchReports(); if (t.id === 'legal') fetchLegal(); if (t.id === 'promos') fetchPromos(); if (t.id === 'analytics') { fetchAnalytics(analyticsRange); fetchGrowth(growthRange); } if (t.id === 'audit') fetchAudit(auditFilter); }}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                tab === t.id ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20' : 'bg-white/5 text-[#9EABCE] hover:text-white'
              }`}
            >
              <t.icon size={18} weight={tab === t.id ? 'fill' : 'light'} />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {tab === 'overview' && stats && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <StatsCard icon={Users} label="Total Users" value={stats.total_users} color="#D4AF37" />
                  <StatsCard icon={Users} label="Pending Profile" value={stats.pending_users ?? 0} color="#FB923C" />
                  <StatsCard icon={Crown} label="Premium Users" value={stats.premium_users} color="#F0C84A" />
                  <StatsCard icon={ChatCircle} label="Total Chats" value={stats.total_chats} color="#60A5FA" />
                  <StatsCard icon={Heart} label="Total Messages" value={stats.total_messages} color="#F472B6" />
                  <StatsCard icon={Flag} label="Reports" value={stats.total_reports} color="#EF4444" />
                </div>

                {/* Payment Rescue Panel — manual Premium grant for payments that didn't auto-upgrade */}
                <div className="glass-card p-6" data-testid="payment-rescue-panel">
                  <div className="flex items-center gap-2 mb-1">
                    <Crown size={20} weight="light" className="text-[#D4AF37]" />
                    <h3 className="text-white font-semibold font-['Outfit']">Payment Rescue</h3>
                  </div>
                  <p className="text-[#9EABCE] text-xs mb-4">
                    Use this when a user paid but the automatic Premium upgrade didn't fire (webhook edge cases, subscription mode drift, etc).
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* By email */}
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-[10px] uppercase tracking-wider text-[#9EABCE] mb-2">Grant by email</p>
                      <Input
                        value={grantEmail}
                        onChange={(e) => setGrantEmail(e.target.value)}
                        placeholder="user@example.com"
                        data-testid="rescue-email-input"
                        className="bg-[#060A1A]/50 border-white/10 text-white text-sm mb-2"
                      />
                      <button
                        onClick={handleGrantPremium}
                        disabled={grantBusy || !grantEmail.trim()}
                        data-testid="rescue-grant-btn"
                        className="w-full btn-gold px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-50"
                      >
                        {grantBusy ? 'Working…' : 'Grant Premium + Verified'}
                      </button>
                    </div>

                    {/* By Stripe session_id */}
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-[10px] uppercase tracking-wider text-[#9EABCE] mb-2">Sync from Stripe session_id</p>
                      <Input
                        value={syncSessionId}
                        onChange={(e) => setSyncSessionId(e.target.value)}
                        placeholder="cs_live_…"
                        data-testid="rescue-session-input"
                        className="bg-[#060A1A]/50 border-white/10 text-white text-sm mb-2 font-mono"
                      />
                      <button
                        onClick={handleSyncFromSession}
                        disabled={grantBusy || !syncSessionId.trim()}
                        data-testid="rescue-sync-btn"
                        className="w-full px-3 py-2 rounded-lg text-xs font-bold bg-white/10 text-white hover:bg-white/15 disabled:opacity-50"
                      >
                        {grantBusy ? 'Working…' : 'Look up + Upgrade from Stripe'}
                      </button>
                    </div>
                  </div>

                  {grantResult && (
                    <div
                      data-testid="rescue-result"
                      className={`mt-4 p-3 rounded-xl text-xs ${
                        grantResult.ok
                          ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                          : 'bg-red-500/10 border border-red-500/30 text-red-300'
                      }`}
                    >
                      {grantResult.msg}
                    </div>
                  )}
                </div>

                {/* Orphan Chats Maintenance Panel */}
                <div className="glass-card p-6" data-testid="orphan-chats-panel">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Trash size={20} weight="light" className="text-[#EF4444]" />
                        <h3 className="text-white font-semibold font-['Outfit']">Database Maintenance — Orphan Chats</h3>
                      </div>
                      <p className="text-[#9EABCE] text-xs">
                        Chats whose participants no longer exist (deleted users). Hidden from the UI but still in the database.
                      </p>
                    </div>
                    <button
                      onClick={fetchOrphans}
                      disabled={orphanLoading}
                      data-testid="scan-orphans-btn"
                      className="px-4 py-2 rounded-xl text-sm font-medium bg-white/5 text-white hover:bg-white/10 transition-all disabled:opacity-50"
                    >
                      {orphanLoading ? 'Scanning...' : (orphanData ? 'Re-scan' : 'Scan for Orphans')}
                    </button>
                  </div>

                  {orphanData && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/5 rounded-xl p-4">
                          <p className="text-2xl font-bold text-white font-['Outfit']" data-testid="orphan-chat-count">
                            {orphanData.orphan_chat_count}
                          </p>
                          <p className="text-xs text-[#9EABCE]">Orphan chats</p>
                        </div>
                        <div className="bg-white/5 rounded-xl p-4">
                          <p className="text-2xl font-bold text-white font-['Outfit']" data-testid="orphan-message-count">
                            {orphanData.orphan_message_count}
                          </p>
                          <p className="text-xs text-[#9EABCE]">Orphan messages</p>
                        </div>
                      </div>

                      {orphanData.orphan_chat_count > 0 && (
                        <>
                          <div className="max-h-48 overflow-y-auto bg-white/[0.02] rounded-xl border border-white/5 p-3 space-y-1">
                            {orphanData.chats.slice(0, 20).map(c => (
                              <div key={c.id} className="text-xs text-[#9EABCE] font-mono flex items-center justify-between gap-3 py-1 border-b border-white/5 last:border-0">
                                <span className="truncate">{c.id.slice(0, 8)}... → missing: {c.missing_users[0]?.slice(0, 8)}...</span>
                                <span className="text-[#9EABCE]/60 whitespace-nowrap truncate max-w-[40%]">{c.last_message || '—'}</span>
                              </div>
                            ))}
                            {orphanData.chats.length > 20 && (
                              <p className="text-xs text-[#9EABCE]/60 pt-2 text-center">+ {orphanData.chats.length - 20} more</p>
                            )}
                          </div>

                          <button
                            onClick={handlePurgeOrphans}
                            disabled={purging}
                            data-testid="purge-orphans-btn"
                            className="w-full px-4 py-3 rounded-xl text-sm font-bold bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20 hover:bg-[#EF4444]/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <Trash size={16} weight="fill" />
                            {purging ? 'Purging...' : `Purge ${orphanData.orphan_chat_count} chats + ${orphanData.orphan_message_count} messages`}
                          </button>
                        </>
                      )}

                      {orphanData.orphan_chat_count === 0 && (
                        <div className="bg-[#34D399]/5 border border-[#34D399]/20 rounded-xl p-4 text-center">
                          <p className="text-[#34D399] text-sm font-medium">Database is clean. No orphan chats found.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Analytics Tab */}
            {tab === 'analytics' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="analytics-tab">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-white font-['Outfit']">Event Analytics</h2>
                    <p className="text-xs text-[#9EABCE] mt-0.5">Page views, user actions & funnel — owned by you, no third party.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {[7, 30, 90].map(d => (
                      <button
                        key={d}
                        onClick={() => { setAnalyticsRange(d); fetchAnalytics(d); }}
                        data-testid={`analytics-range-${d}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          analyticsRange === d
                            ? 'bg-[#D4AF37] text-[#060A1A]'
                            : 'bg-white/5 text-[#9EABCE] hover:text-white'
                        }`}
                      >
                        {d}d
                      </button>
                    ))}
                  </div>
                </div>

                {analyticsLoading && !analytics ? (
                  <div className="flex justify-center py-12">
                    <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : !analytics || analytics.total_events === 0 ? (
                  <div className="glass-card p-10 text-center">
                    <ChartLineUp size={40} weight="light" className="text-[#9EABCE]/30 mx-auto mb-3" />
                    <p className="text-white font-medium">No events logged in this window yet</p>
                    <p className="text-[#9EABCE] text-xs mt-1">Events populate as users browse, blink, chat, and purchase Premium.</p>
                  </div>
                ) : (
                  <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <StatsCard icon={ChartLineUp} label="Total Events" value={analytics.total_events} color="#D4AF37" />
                      <StatsCard icon={ChartBar} label="Page Views" value={(analytics.by_name.find(x => x.name === 'page_view') || {}).count || 0} color="#60A5FA" />
                      <StatsCard icon={Heart} label="Blinks Sent" value={(analytics.by_name.find(x => x.name === 'blink_sent') || {}).count || 0} color="#F472B6" />
                      <StatsCard icon={Crown} label="Premium Clicks" value={(analytics.by_name.find(x => x.name === 'premium_clicked') || {}).count || 0} color="#F0C84A" />
                    </div>

                    {/* Session metrics */}
                    {(() => {
                      const fmt = (s) => {
                        s = Math.round(s || 0);
                        if (s < 60) return `${s}s`;
                        const m = Math.floor(s / 60);
                        const sec = s % 60;
                        return `${m}m ${sec}s`;
                      };
                      const br = Math.round((analytics.bounce_rate || 0) * 100);
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-session-metrics">
                          <StatsCard icon={UsersThree} label="Sessions" value={analytics.total_sessions || 0} color="#A78BFA" />
                          <StatsCard icon={Clock} label="Avg Session" value={fmt(analytics.avg_session_duration_sec)} color="#D4AF37" />
                          <StatsCard icon={Clock} label="Median Session" value={fmt(analytics.median_session_duration_sec)} color="#F0C84A" />
                          <StatsCard icon={ChartBar} label="Bounce Rate" value={`${br}%`} color="#F97316" />
                        </div>
                      );
                    })()}

                    {/* Funnel */}
                    <div className="glass-card p-6" data-testid="analytics-funnel">
                      <h3 className="text-white font-semibold mb-4 font-['Outfit']">Conversion Funnel</h3>
                      {(() => {
                        const top = Math.max(1, ...(analytics.funnel || []).map(f => f.count));
                        const labels = {
                          page_view: 'Visits',
                          blink_sent: 'Blinks',
                          chat_opened: 'Chats Opened',
                          premium_clicked: 'Premium Clicks',
                          premium_purchased: 'Premium Purchased',
                        };
                        return (
                          <div className="space-y-2">
                            {(analytics.funnel || []).map((f, i) => {
                              const pct = Math.max(4, (f.count / top) * 100);
                              const firstCount = analytics.funnel[0]?.count || 0;
                              const conv = firstCount ? ((f.count / firstCount) * 100).toFixed(1) : '0.0';
                              return (
                                <div key={f.name} className="flex items-center gap-3">
                                  <span className="text-xs text-[#9EABCE] w-36 flex-shrink-0">{labels[f.name] || f.name}</span>
                                  <div className="flex-1 h-7 bg-white/5 rounded-lg overflow-hidden relative">
                                    <div
                                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F0C84A] rounded-lg flex items-center px-3"
                                      style={{ width: `${pct}%` }}
                                    >
                                      <span className="text-[#060A1A] font-bold text-xs">{f.count.toLocaleString()}</span>
                                    </div>
                                  </div>
                                  <span className="text-xs text-[#9EABCE] w-14 text-right">{i === 0 ? '100%' : `${conv}%`}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Events breakdown + Top pages */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="glass-card p-5" data-testid="analytics-by-name">
                        <h3 className="text-white font-semibold mb-3 font-['Outfit']">All Events ({analyticsRange}d)</h3>
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                          {analytics.by_name.map(e => (
                            <div key={e.name} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-white/5 transition-all">
                              <code className="text-xs text-[#D4AF37] font-mono truncate">{e.name}</code>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-[10px] text-[#9EABCE]">{e.unique_users} users</span>
                                <span className="text-sm font-bold text-white">{e.count.toLocaleString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="glass-card p-5" data-testid="analytics-top-pages">
                        <h3 className="text-white font-semibold mb-3 font-['Outfit']">Top Pages</h3>
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                          {analytics.top_pages.length === 0 && (
                            <p className="text-xs text-[#9EABCE]/60">No page views yet.</p>
                          )}
                          {analytics.top_pages.map((p, i) => (
                            <div key={`${p.path}-${i}`} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-white/5 transition-all">
                              <span className="text-xs text-white truncate">{p.path || '/'}</span>
                              <span className="text-sm font-bold text-[#D4AF37]">{p.count.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Daily trend (bar chart) */}
                    {analytics.daily && analytics.daily.length > 0 && (
                      <div className="glass-card p-5" data-testid="analytics-daily">
                        <h3 className="text-white font-semibold mb-4 font-['Outfit']">Daily Events</h3>
                        {(() => {
                          const maxD = Math.max(1, ...analytics.daily.map(d => d.count));
                          return (
                            <div className="flex items-end gap-1 h-40">
                              {analytics.daily.map(d => (
                                <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group">
                                  <span className="text-[9px] text-[#9EABCE] opacity-0 group-hover:opacity-100 transition-opacity">{d.count}</span>
                                  <div
                                    className="w-full bg-gradient-to-t from-[#D4AF37] to-[#F0C84A] rounded-t-md min-h-[4px] hover:opacity-80 transition-opacity"
                                    style={{ height: `${(d.count / maxD) * 100}%` }}
                                    title={`${d.day}: ${d.count}`}
                                  />
                                  <span className="text-[9px] text-[#9EABCE]/60 truncate w-full text-center">{d.day.slice(5)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* ─── Growth Metrics ─── */}
                    <div className="pt-4 border-t border-white/5" data-testid="analytics-growth">
                      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-white font-['Outfit'] flex items-center gap-2">
                            <ChartLineUp size={20} weight="fill" className="text-[#D4AF37]" /> Growth
                          </h3>
                          <p className="text-xs text-[#9EABCE] mt-0.5">DAU / MAU, retention, ARPU & page engagement.</p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={async () => {
                              try {
                                const { data } = await api.post('/admin/growth-email/send-preview');
                                alert(`Weekly growth email sent to ${data.sent_to}`);
                              } catch (e) {
                                alert(`Failed: ${e?.response?.data?.detail || e.message}`);
                              }
                            }}
                            data-testid="send-growth-email-preview"
                            className="px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/25 text-xs font-bold hover:bg-[#D4AF37]/20 transition-all flex items-center gap-1.5"
                            title="Send yourself a preview of the weekly growth email"
                          >
                            ✉ Email me preview
                          </button>
                          {[30, 60, 90, 180].map(d => (
                            <button
                              key={d}
                              onClick={() => { setGrowthRange(d); fetchGrowth(d); }}
                              data-testid={`growth-range-${d}`}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                growthRange === d
                                  ? 'bg-[#D4AF37] text-[#060A1A]'
                                  : 'bg-white/5 text-[#9EABCE] hover:text-white'
                              }`}
                            >
                              {d}d
                            </button>
                          ))}
                        </div>
                      </div>                      {growthLoading && !growth ? (
                        <div className="flex justify-center py-8">
                          <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : !growth ? (
                        <p className="text-sm text-[#9EABCE]/60">No growth data yet.</p>
                      ) : (
                        <>
                          {/* Summary cards for growth */}
                          {(() => {
                            const lastDau = growth.dau?.[growth.dau.length - 1]?.count || 0;
                            const lastMau = growth.mau?.[growth.mau.length - 1]?.count || 0;
                            const lastArpu = growth.arpu?.[growth.arpu.length - 1]?.arpu || 0;
                            const rev = growth.total_revenue || 0;
                            return (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                                <StatsCard icon={UsersThree} label="DAU (today)" value={lastDau} color="#60A5FA" />
                                <StatsCard icon={Users} label="MAU (today)" value={lastMau} color="#A78BFA" />
                                <StatsCard icon={CurrencyEur} label={`Revenue (${growthRange}d)`} value={`€${rev.toFixed(2)}`} color="#D4AF37" />
                                <StatsCard icon={CurrencyEur} label="ARPU (today)" value={`€${lastArpu.toFixed(2)}`} color="#F0C84A" />
                              </div>
                            );
                          })()}

                          {/* DAU / MAU chart */}
                          <div className="glass-card p-5 mb-4" data-testid="chart-dau-mau">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-white font-semibold text-sm font-['Outfit']">Daily / Monthly Active Users</h4>
                            </div>
                            <LineChart
                              height={200}
                              series={[
                                { name: 'DAU', color: '#60A5FA', data: (growth.dau || []).map(d => ({ x: d.day, y: d.count })) },
                                { name: 'MAU (30d rolling)', color: '#D4AF37', data: (growth.mau || []).map(d => ({ x: d.day, y: d.count })) },
                              ]}
                            />
                          </div>

                          {/* Retention chart */}
                          <div className="glass-card p-5 mb-4" data-testid="chart-retention">
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                              <h4 className="text-white font-semibold text-sm font-['Outfit'] flex items-center gap-2">
                                <Repeat size={16} weight="fill" className="text-[#D4AF37]" /> Retention by Signup Cohort
                              </h4>
                              {growth.overall_retention && (
                                <div className="flex items-center gap-4 text-xs">
                                  <span className="text-[#9EABCE]">Avg:</span>
                                  <span className="text-white"><b className="text-[#60A5FA]">D1</b> {Math.round((growth.overall_retention.d1 || 0) * 100)}%</span>
                                  <span className="text-white"><b className="text-[#D4AF37]">D7</b> {Math.round((growth.overall_retention.d7 || 0) * 100)}%</span>
                                  <span className="text-white"><b className="text-[#F472B6]">D30</b> {Math.round((growth.overall_retention.d30 || 0) * 100)}%</span>
                                </div>
                              )}
                            </div>
                            <LineChart
                              height={200}
                              yFormat={(v) => `${Math.round(v)}%`}
                              series={[
                                { name: 'D1', color: '#60A5FA', data: (growth.retention || []).map(r => ({ x: r.cohort, y: r.d1 === null ? null : Math.round(r.d1 * 100) })) },
                                { name: 'D7', color: '#D4AF37', data: (growth.retention || []).map(r => ({ x: r.cohort, y: r.d7 === null ? null : Math.round(r.d7 * 100) })) },
                                { name: 'D30', color: '#F472B6', data: (growth.retention || []).map(r => ({ x: r.cohort, y: r.d30 === null ? null : Math.round(r.d30 * 100) })) },
                              ]}
                              emptyMsg="No signup cohorts yet — retention appears after the first users sign up."
                            />
                          </div>

                          {/* ARPU chart */}
                          <div className="glass-card p-5 mb-4" data-testid="chart-arpu">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-white font-semibold text-sm font-['Outfit'] flex items-center gap-2">
                                <CurrencyEur size={16} weight="fill" className="text-[#D4AF37]" /> ARPU — Daily Revenue per Active User
                              </h4>
                            </div>
                            <LineChart
                              height={200}
                              yFormat={(v) => `€${Number(v).toFixed(2)}`}
                              series={[
                                { name: 'ARPU', color: '#D4AF37', data: (growth.arpu || []).map(a => ({ x: a.day, y: a.arpu })) },
                              ]}
                              emptyMsg="No paid transactions in this window."
                            />
                          </div>

                          {/* Page engagement */}
                          <div className="glass-card p-5" data-testid="page-engagement">
                            <h4 className="text-white font-semibold text-sm font-['Outfit'] mb-3 flex items-center gap-2">
                              <Clock size={16} weight="fill" className="text-[#D4AF37]" /> Most Engaging Pages (avg time on page)
                            </h4>
                            {(!growth.page_engagement || growth.page_engagement.length === 0) ? (
                              <p className="text-xs text-[#9EABCE]/60">No `page_exit` data yet — events will populate as users navigate.</p>
                            ) : (
                              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {(() => {
                                  const top = growth.page_engagement[0]?.avg_duration_ms || 1;
                                  return growth.page_engagement.map((p) => {
                                    const s = (p.avg_duration_ms || 0) / 1000;
                                    const fmt = s < 60 ? `${Math.round(s)}s` : `${Math.floor(s / 60)}m ${Math.round(s) % 60}s`;
                                    const width = Math.max(6, (p.avg_duration_ms / top) * 100);
                                    return (
                                      <div key={p.path} className="flex items-center gap-3">
                                        <span className="text-xs text-white w-44 truncate flex-shrink-0">{p.path || '/'}</span>
                                        <div className="flex-1 h-6 bg-white/5 rounded-md overflow-hidden">
                                          <div className="h-full bg-gradient-to-r from-[#D4AF37]/70 to-[#F0C84A]/70 rounded-md" style={{ width: `${width}%` }} />
                                        </div>
                                        <span className="text-xs font-bold text-white w-14 text-right">{fmt}</span>
                                        <span className="text-[10px] text-[#9EABCE] w-14 text-right">{p.samples} v</span>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* Users Tab */}
            {tab === 'users' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9EABCE]" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                      placeholder="Search by name or email..."
                      data-testid="admin-search"
                      className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-xl"
                    />
                  </div>
                  <div className="flex gap-1 bg-white/5 rounded-xl p-1" data-testid="admin-status-filter">
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'complete', label: 'Complete' },
                      { id: 'pending', label: 'Pending' }
                    ].map(f => (
                      <button
                        key={f.id}
                        onClick={() => setUserStatusFilter(f.id)}
                        data-testid={`admin-filter-${f.id}`}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                          userStatusFilter === f.id ? 'bg-[#D4AF37] text-[#060A1A]' : 'text-[#9EABCE] hover:bg-white/5'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={fetchUsers}
                    data-testid="admin-refresh-users"
                    className="px-4 py-2 rounded-xl text-xs font-medium bg-white/5 text-white hover:bg-white/10 transition-all"
                  >
                    Refresh
                  </button>
                </div>

                {/* Quick sort */}
                <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="admin-users-sort">
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9EABCE]/60">Sort</span>
                  {[
                    { id: 'newest', label: 'Newest' },
                    { id: 'oldest', label: 'Oldest' },
                    { id: 'active', label: 'Recently active' },
                  ].map(s => (
                    <button
                      key={s.id}
                      onClick={() => setUsersSort(s.id)}
                      data-testid={`admin-users-sort-${s.id}`}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                        usersSort === s.id ? 'bg-[#D4AF37] text-[#060A1A]' : 'bg-white/5 text-[#9EABCE] hover:bg-white/10'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#9EABCE]/60 mb-3" data-testid="admin-users-count">
                  {usersTotal === 0 ? 'No users found' : (
                    <>Showing <span className="text-white font-semibold">{(usersPage - 1) * USERS_PER_PAGE + 1}</span>–<span className="text-white font-semibold">{Math.min(usersPage * USERS_PER_PAGE, usersTotal)}</span> of <span className="text-white font-semibold">{usersTotal}</span></>
                  )}
                </p>
                <div className="space-y-2">
                  {users.map(u => (
                    <div key={u.id} className="glass-card p-4 flex items-center gap-4" data-testid={`admin-user-${u.id}`}>
                      <button
                        type="button"
                        onClick={() => navigate(`/profile/${u.id}`)}
                        data-testid={`admin-user-link-${u.id}`}
                        className="flex items-center gap-4 flex-1 min-w-0 text-left rounded-lg -m-1 p-1 hover:bg-white/5 transition-all group"
                        title="Open profile"
                      >
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-white/5 ring-0 group-hover:ring-2 group-hover:ring-[#D4AF37]/40 transition-all">
                          {u.photos?.[0] ? (
                            <img src={getPhotoSrc(u.photos[0])} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#9EABCE] text-sm font-bold">
                              {u.first_name?.[0] || '?'}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-semibold text-sm group-hover:text-[#D4AF37] transition-colors">
                              {u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : <span className="text-[#9EABCE]/60 italic">(no name yet)</span>}
                            </span>
                            {u.is_admin && <Badge className="bg-[#D4AF37] text-[#060A1A] text-[10px]">Admin</Badge>}
                            {u.is_premium && <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] text-[10px]">Premium</Badge>}
                            {u.is_verified && <Badge className="bg-green-500/20 text-green-400 text-[10px]">Verified</Badge>}
                            {u.is_banned && <Badge className="bg-red-500/20 text-red-400 text-[10px]">Banned</Badge>}
                            {!u.profile_complete && <Badge className="bg-orange-500/15 text-orange-400 text-[10px]" data-testid={`admin-pending-badge-${u.id}`}>Pending Profile</Badge>}
                          </div>
                          <p className="text-xs text-[#9EABCE] truncate">{u.email}</p>
                        </div>
                      </button>
                      <div className="flex gap-1.5 flex-shrink-0">
                        {!u.is_verified ? (
                          <button onClick={() => handleVerify(u.id)} className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all" title="Verify">
                            <ShieldCheck size={16} />
                          </button>
                        ) : (
                          <button onClick={() => handleUnverify(u.id)} className="p-2 rounded-lg bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 transition-all" title="Revoke Verification">
                            <ShieldCheck size={16} />
                          </button>
                        )}
                        {u.is_banned ? (
                          <button onClick={() => handleUnban(u.id)} className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all" title="Unban">
                            <Users size={16} />
                          </button>
                        ) : (
                          <button onClick={() => handleBan(u.id)} className="p-2 rounded-lg bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-all" title="Ban">
                            <Prohibit size={16} />
                          </button>
                        )}
                        {!u.is_admin && (
                          <button onClick={() => handleDelete(u.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all" title="Delete">
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {usersPages > 1 && (
                  <div className="flex items-center justify-between gap-2 mt-4 flex-wrap" data-testid="admin-users-pagination">
                    <button
                      onClick={() => fetchUsers(Math.max(1, usersPage - 1))}
                      disabled={usersPage <= 1}
                      data-testid="admin-users-prev"
                      className="px-4 py-2 rounded-xl text-xs font-semibold bg-white/5 text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ← Prev
                    </button>
                    <div className="flex items-center gap-1 flex-wrap">
                      {Array.from({ length: usersPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === usersPages || Math.abs(p - usersPage) <= 2)
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, i) => p === '...' ? (
                          <span key={`gap-${i}`} className="px-2 text-[#9EABCE]/50 text-xs">…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => fetchUsers(p)}
                            data-testid={`admin-users-page-${p}`}
                            className={`min-w-[32px] h-8 rounded-lg text-xs font-semibold transition-all ${
                              p === usersPage ? 'bg-[#D4AF37] text-[#060A1A]' : 'bg-white/5 text-[#9EABCE] hover:bg-white/10'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                    </div>
                    <button
                      onClick={() => fetchUsers(Math.min(usersPages, usersPage + 1))}
                      disabled={usersPage >= usersPages}
                      data-testid="admin-users-next"
                      className="px-4 py-2 rounded-xl text-xs font-semibold bg-white/5 text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Reports Tab */}
            {tab === 'reports' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                {reports.length === 0 ? (
                  <div className="text-center py-12">
                    <Flag size={40} weight="light" className="text-[#9EABCE]/20 mx-auto mb-3" />
                    <p className="text-[#9EABCE]/50">No reports yet</p>
                  </div>
                ) : (
                  reports.map(r => (
                    <div key={r.id} className="glass-card p-4" data-testid={`admin-report-${r.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Flag size={16} weight="fill" className="text-red-400" />
                        <span className="text-white text-sm font-semibold">
                          {r.reporter?.first_name || 'Unknown'} reported {r.reported_user?.first_name || 'Unknown'}
                        </span>
                        <span className="text-[#9EABCE]/40 text-xs ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-[#9EABCE] text-sm">{r.reason}</p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleBan(r.reported_user_id)}
                          className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-all"
                        >
                          Ban Reported User
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </motion.div>
            )}

            {/* Legal CMS Tab */}
            {tab === 'legal' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                {editingLegal ? (
                  <div className="glass-card p-6 space-y-4" data-testid="legal-editor">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-semibold font-['Outfit']">Editing: {editingLegal}</h3>
                      <button onClick={() => setEditingLegal(null)} className="text-[#9EABCE] hover:text-white text-sm">Cancel</button>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">Title</label>
                      <Input value={legalTitle} onChange={(e) => setLegalTitle(e.target.value)} data-testid="legal-title-input" className="bg-white/5 border-white/10 text-white rounded-xl" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-2 block">Content (Markdown)</label>
                      <textarea
                        value={legalContent}
                        onChange={(e) => setLegalContent(e.target.value)}
                        rows={18}
                        data-testid="legal-content-input"
                        className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-4 text-sm font-mono resize-y focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                      />
                    </div>
                    <button onClick={handleSaveLegal} disabled={savingLegal} data-testid="legal-save-btn" className="btn-gold px-6 py-3 rounded-xl font-bold disabled:opacity-50">
                      {savingLegal ? 'Saving...' : 'Save & Publish'}
                    </button>
                  </div>
                ) : (
                  ['impressum', 'datenschutz', 'agb'].map(pageType => {
                    const existing = legalPages[pageType];
                    return (
                      <div key={pageType} className="glass-card p-5 flex items-center justify-between" data-testid={`legal-card-${pageType}`}>
                        <div>
                          <p className="text-white font-semibold capitalize">{pageType === 'datenschutz' ? 'Privacy Policy (GDPR)' : pageType === 'agb' ? 'Terms of Service (AGB)' : 'Impressum'}</p>
                          <p className="text-xs text-[#9EABCE] mt-1">
                            {existing ? `Last updated: ${new Date(existing.updated_at).toLocaleDateString()}` : 'Using default content — click Edit to customize'}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setEditingLegal(pageType);
                            setLegalTitle(existing?.title || (pageType === 'datenschutz' ? 'Datenschutz (GDPR)' : pageType === 'agb' ? 'AGB (Terms of Service)' : 'Impressum'));
                            setLegalContent(existing?.content || '');
                          }}
                          data-testid={`legal-edit-${pageType}`}
                          className="btn-outline-gold px-4 py-2 text-sm rounded-xl font-semibold"
                        >
                          Edit
                        </button>
                      </div>
                    );
                  })
                )}
              </motion.div>
            )}

            {/* Promos Tab */}
            {tab === 'promos' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-white font-semibold font-['Outfit']">Launch Promo</h3>
                      <p className="text-xs text-[#9EABCE] mt-1">50% off first month (code: LAUNCH50)</p>
                    </div>
                    <button onClick={handleCreatePromo} data-testid="create-promo-btn" className="btn-gold px-4 py-2 text-sm rounded-xl font-bold">
                      Create LAUNCH50
                    </button>
                  </div>

                  {promos.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {promos.map(p => (
                        <div key={p.code} className="flex items-center justify-between p-3 bg-white/5 rounded-xl" data-testid={`promo-${p.code}`}>
                          <div className="flex items-center gap-3">
                            <Tag size={20} weight="fill" className="text-[#D4AF37]" />
                            <div>
                              <span className="text-white font-bold font-mono">{p.code}</span>
                              <span className="text-[#9EABCE] text-sm ml-2">({p.discount_percent}% off)</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-bold px-2 py-1 rounded-lg ${p.active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                              {p.active ? 'ACTIVE' : 'DISABLED'}
                            </span>
                            <button onClick={() => handleTogglePromo(p.code)} className="text-xs text-[#9EABCE] hover:text-white underline">
                              {p.active ? 'Disable' : 'Enable'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="glass-card p-6">
                  <h3 className="text-white font-semibold font-['Outfit'] mb-2">How users apply the promo</h3>
                  <p className="text-[#9EABCE] text-sm">Users enter the code <span className="text-[#D4AF37] font-mono font-bold">LAUNCH50</span> on the Premium page before checkout. The price drops from 10.00 EUR to <span className="text-[#D4AF37] font-bold">5.00 EUR</span> for the first month.</p>
                </div>
              </motion.div>
            )}

            {/* Audit Log Tab */}
            {tab === 'audit' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="admin-audit-tab">
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                      <h3 className="text-white font-semibold font-['Outfit'] flex items-center gap-2">
                        <FileText size={18} weight="fill" className="text-[#D4AF37]" />
                        Admin Audit Log
                      </h3>
                      <p className="text-xs text-[#9EABCE] mt-1">Every moderation and impersonation action taken by an admin.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {['', 'impersonate', 'ban', 'unban', 'verify', 'unverify', 'grant_premium', 'revoke_premium', 'welcome_email'].map(a => (
                        <button
                          key={a || 'all'}
                          onClick={() => { setAuditFilter(a); fetchAudit(a); }}
                          data-testid={`audit-filter-${a || 'all'}`}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${
                            auditFilter === a ? 'bg-[#D4AF37] text-[#060A1A]' : 'bg-white/5 text-[#9EABCE] hover:bg-white/10'
                          }`}
                        >
                          {a || 'All'}
                        </button>
                      ))}
                      <button
                        onClick={() => fetchAudit(auditFilter)}
                        data-testid="audit-refresh"
                        className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-white/5 text-[#9EABCE] hover:bg-white/10 transition-all"
                      >
                        ↻
                      </button>
                    </div>
                  </div>

                  {auditLoading ? (
                    <div className="flex justify-center py-10">
                      <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : auditRows.length === 0 ? (
                    <p className="text-sm text-[#9EABCE] py-10 text-center">No audit entries yet.</p>
                  ) : (
                    <div className="overflow-x-auto -mx-2">
                      <table className="w-full text-sm" data-testid="audit-table">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-[#9EABCE]/70">
                            <th className="text-left px-2 py-2 font-semibold">When</th>
                            <th className="text-left px-2 py-2 font-semibold">Admin</th>
                            <th className="text-left px-2 py-2 font-semibold">Action</th>
                            <th className="text-left px-2 py-2 font-semibold">Target</th>
                            <th className="text-left px-2 py-2 font-semibold">IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditRows.map((r, i) => (
                            <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                              <td className="px-2 py-2 text-[#9EABCE] whitespace-nowrap">
                                {r.ts ? new Date(r.ts).toLocaleString() : '—'}
                              </td>
                              <td className="px-2 py-2 text-white truncate max-w-[200px]">{r.admin_email || '—'}</td>
                              <td className="px-2 py-2">
                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                                  r.action === 'impersonate' ? 'bg-purple-500/15 text-purple-300' :
                                  r.action === 'ban' ? 'bg-red-500/15 text-red-400' :
                                  r.action === 'unban' ? 'bg-green-500/15 text-green-400' :
                                  r.action === 'verify' ? 'bg-green-500/15 text-green-400' :
                                  r.action === 'unverify' ? 'bg-white/10 text-[#9EABCE]' :
                                  r.action === 'grant_premium' ? 'bg-[#D4AF37]/15 text-[#D4AF37]' :
                                  r.action === 'revoke_premium' ? 'bg-white/10 text-[#9EABCE]' :
                                  r.action === 'welcome_email' ? 'bg-blue-500/15 text-blue-300' :
                                  'bg-white/10 text-[#9EABCE]'
                                }`}>
                                  {r.action}
                                </span>
                              </td>
                              <td className="px-2 py-2 text-[#9EABCE]">
                                {r.target_id ? (
                                  <button
                                    onClick={() => navigate(`/profile/${r.target_id}`)}
                                    className="hover:text-[#D4AF37] transition-colors truncate"
                                  >
                                    {r.target_email || r.target_id.slice(0, 8)}
                                  </button>
                                ) : '—'}
                              </td>
                              <td className="px-2 py-2 text-[#9EABCE]/70 text-xs font-mono">{r.ip || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
